import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, Trash2, MapPin, Calendar, User, Image as ImageIcon, X, Pencil, Mail } from "lucide-react";
import { parseLocalDate } from "../utils/date";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function MaquillajeDetail() {
  const { id } = useParams();
  const { authHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/maquillajes/${id}`, { headers: authHeaders() });
        setM(data);
      } catch {
        toast.error("Diagnóstico no encontrado");
        navigate("/maquillajes");
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const del = async () => {
    try {
      await axios.delete(`${API}/maquillajes/${id}`, { headers: authHeaders() });
      toast.success("Diagnóstico eliminado");
      navigate("/maquillajes");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  if (loading) return <div className="p-10 kbd-label text-slate-400">Cargando…</div>;
  if (!m) return null;

  const photoUrl = (path) => `${API}/files/${path}`;

  return (
    <div className="p-5 md:p-8 lg:p-12 max-w-5xl mx-auto" data-testid="maquillaje-detail-page">
      <button onClick={() => navigate(-1)} className="kbd-label flex items-center gap-2 mb-6 text-slate-500 hover:text-slate-900" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="kbd-label mb-2 text-fuchsia-700">/ Diagnóstico de Maquillaje</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-slate-950">{m.client_name || "Sin nombre"}</h1>
          <div className="flex flex-wrap gap-2 mt-4 text-sm text-slate-600">
            <InfoChip icon={Calendar} label={parseLocalDate(m.fecha).toLocaleDateString("es", { day: "numeric", month: "long", year: "numeric" })} />
            {m.client_email && <InfoChip icon={Mail} label={m.client_email} />}
            {m.branch && <InfoChip icon={MapPin} label={m.branch} />}
            {m.advisor && <InfoChip icon={User} label={`Consejera: ${m.advisor}`} />}
            <InfoChip icon={User} label={m.user_name} />
          </div>
        </div>
        {(user?.role === "admin" || user?.id === m.user_id) && (
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => navigate(`/maquillajes/${id}/edit`)} className="gap-2 border-fuchsia-200 text-fuchsia-800 hover:bg-fuchsia-50" data-testid="edit-maquillaje-btn">
              <Pencil className="w-4 h-4" /> Editar
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800" data-testid="delete-maquillaje-btn">
                  <Trash2 className="w-4 h-4" /> Eliminar
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar diagnóstico?</AlertDialogTitle>
                  <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700">Eliminar</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <Block title="Diagnóstico de piel">
          <ListRow label="Tipo de piel" value={m.skin_type} />
          <ListRow label="Necesidades" value={m.skin_needs} />
        </Block>

        <Block title="Base y preparación">
          <ListRow label="Primer" value={m.primer} />
          <TextRow label="Primer - Producto" value={m.primer_product} />
          <TextRow label="Base - Producto" value={m.foundation_product} />
          <TextRow label="Base - Tono" value={m.foundation_shade} />
          <TextRow label="Corrector - Producto" value={m.concealer_product} />
          <TextRow label="Corrector - Tono" value={m.concealer_shade} />
          <ListRow label="Polvo" value={m.powder} />
          <TextRow label="Polvo - Producto" value={m.powder_product} />
          <TextRow label="Polvo - Tono" value={m.powder_shade} />
        </Block>

        <Block title="Rostro">
          <TextRow label="Blush - Producto" value={m.blush_product} />
          <TextRow label="Blush - Tono" value={m.blush_shade} />
          <ListRow label="Contorno" value={m.contour} />
          <TextRow label="Contorno - Producto" value={m.contour_product} />
          <TextRow label="Contorno - Tono" value={m.contour_shade} />
        </Block>

        <Block title="Ojos">
          <TextRow label="Sombras - Producto/Paleta" value={m.eyeshadow_product} />
          <TextRow label="Sombras - Tonos" value={m.eyeshadow_shades} />
          <ListRow label="Delineador" value={m.eyeliner} />
          <TextRow label="Delineador - Producto" value={m.eyeliner_product} />
          <TextRow label="Máscara - Producto" value={m.mascara_product} />
          <ListRow label="Técnica / estilo" value={m.eye_technique} />
          <TextRow label="Técnica - Otro" value={m.eye_technique_other} />
        </Block>

        <Block title="Cejas">
          <TextRow label="Producto" value={m.brow_product} />
          <TextRow label="Tono / Técnica" value={m.brow_shade} />
        </Block>

        <Block title="Labios">
          <TextRow label="Producto" value={m.lip_product} />
          <ListRow label="Tipo" value={m.lip_type} />
          <TextRow label="Tono" value={m.lip_shade} />
          <ListRow label="Acabado" value={m.lip_finish} />
        </Block>

        <Block title="Resultado y seguimiento">
          <ListRow label="Look realizado" value={m.look_done} />
          <TextRow label="Look - Otro" value={m.look_done_other} />
          <TextRow label="Productos de interés" value={m.liked_products} />
          <TextRow label="Recomendación 1" value={m.recommendation_1} />
          <TextRow label="Recomendación 2" value={m.recommendation_2} />
          <TextRow label="Recomendación 3" value={m.recommendation_3} />
          <TextRow label="Observaciones" value={m.observations} />
          <TextRow label="Próximo seguimiento" value={m.next_followup} />
        </Block>
      </div>

      {m.result_photos?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-5 md:p-6 mt-4">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon className="w-4 h-4 text-slate-400" />
            <div className="kbd-label">/ Fotos del resultado · {m.result_photos.length}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {m.result_photos.map((path, i) => (
              <button key={path} onClick={() => setLightbox(path)} className="aspect-square bg-slate-100 rounded-md overflow-hidden hover:ring-2 hover:ring-fuchsia-600" data-testid={`result-photo-${i}`}>
                <img src={photoUrl(path)} alt={`look ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-slate-950/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center"><X className="w-5 h-5" /></button>
          <img src={photoUrl(lightbox)} alt="full" className="max-h-[90vh] max-w-[90vw] object-contain" />
        </div>
      )}
    </div>
  );
}

function Block({ title, children }) {
  const rows = React.Children.toArray(children).filter(Boolean);
  if (rows.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 tv-ticker-line pl-6">
      <div className="kbd-label text-fuchsia-700 mb-3">{title}</div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">{rows}</div>
    </div>
  );
}

function TextRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-sm">
      <span className="kbd-label !text-[10px]">{label}</span>
      <div className="text-slate-900 whitespace-pre-wrap leading-relaxed">{value}</div>
    </div>
  );
}

function ListRow({ label, value }) {
  if (!value || value.length === 0) return null;
  return (
    <div className="text-sm">
      <span className="kbd-label !text-[10px]">{label}</span>
      <div className="flex flex-wrap gap-1.5 mt-1">
        {value.map((v) => (
          <span key={v} className="text-xs bg-fuchsia-50 text-fuchsia-800 border border-fuchsia-200 px-2 py-0.5 rounded-full">{v}</span>
        ))}
      </div>
    </div>
  );
}

function InfoChip({ icon: Icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 rounded-md">
      <Icon className="w-3.5 h-3.5 text-slate-500" />
      <span className="text-xs font-medium text-slate-800">{label}</span>
    </div>
  );
}
